<template>
  <div class="container">
    <div v-if="service" class="container-fluid">
      <div class="row">
        <div class="col my-3 shadow-sm d-flex p-2 align-items-center bg-white">
          <Breadcrumb
            class="directory-nav"
            :crumbs="crumbs"
            useRouterLink
          />
          <check-out class="ml-auto" :bookmark="false" />
        </div>
      </div>

      <div class="row">
        <div class="col p-0">
          <div class="container p-0">
            <div class="row">
              <div class="col-md-8">
                <div class="d-flex flex-row justify-content-between">
                  <ReportTitle type="Service" :name="service.name" />
                  <div>
                    <Button
                      type="outline"
                      :label="isInCart ? 'Remove' : 'Add'"
                      size="sm"
                      @click="toggleSelection"
                    />
                  </div>
                </div>

                <ViewGenerator :viewmodel="serviceViewmodel" />
              </div>
              <!-- Right side card -->
              <ServiceReportInfoCard :service="service" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { QueryEMX2 } from "molgenis-components";
// @ts-ignore
import { Breadcrumb } from "molgenis-components";
import { IBiobanks, IServices } from "../interfaces/directory";
import { getServiceProvider } from "../functions/dataModel";
import {
  buildSelectionPaths,
  getServiceViewmodel,
} from "../functions/serviceViewmodel";
import { useSettingsStore } from "../stores/settingsStore";
import { useRoute } from "vue-router";
import ReportTitle from "../components/report-components/ReportTitle.vue";
import ServiceReportInfoCard from "../components/report-components/ServiceReportInfoCard.vue";
import CheckOut from "../components/checkout-components/CheckOut.vue";
import ViewGenerator from "../components/generators/ViewGenerator.vue";
import Button from "../components/Button.vue";
import { useCheckoutStore } from "../stores/checkoutStore";
import useErrorHandler from "../composables/errorHandler";

const service = ref<IServices | null>(null);
const { setError, clearError } = useErrorHandler();

const settingsStore = useSettingsStore();
const dataModel = settingsStore.config.dataModel;
const providerRef = dataModel.providerRefOnService;

/**
 * The selection is derived from the display configs, so a configured field is
 * always fetched. id/name and the provider ref are added unconditionally:
 * the breadcrumb and the cart need them even when they are not displayed.
 */
const serviceSelection = [
  ...buildSelectionPaths(settingsStore.config.serviceReportColumns, {
    always: ["id", "name", `${providerRef}.id`, `${providerRef}.name`],
  }),
  ...buildSelectionPaths(settingsStore.config.serviceProviderReportColumns, {
    prefix: providerRef,
    always: [`${providerRef}.research_infrastructure`],
  }),
  ...settingsStore.config.serviceContactColumns,
];

new QueryEMX2(settingsStore.config.graphqlEndpoint)
  .table(dataModel.serviceTable)
  .select(serviceSelection)
  .where("id")
  .equals(useRoute().params.id)
  .execute()
  .then((data: any) => {
    clearError();
    if (data[dataModel.serviceTable]?.length) {
      service.value = data[dataModel.serviceTable][0];
    } else {
      setError("Service not found");
    }
  })
  .catch((error) => {
    setError(error);
  });

const serviceProvider = computed(() => getServiceProvider(service.value));

const serviceViewmodel = computed(() =>
  getServiceViewmodel(service.value, settingsStore.config.serviceReportColumns)
);

const crumbs = computed(() => {
  const nextCrumbs: Record<string, string> = {
    ["Back to catalogue"]: "../",
  };
  if (serviceProvider.value) {
    const crumbLabel =
      serviceProvider.value.research_infrastructure ||
      serviceProvider.value.name;
    nextCrumbs[crumbLabel] = `../biobank/${serviceProvider.value.id}`;
  }
  if (service.value) {
    nextCrumbs[service.value.name] = `../service/${service.value.id}`;
  }
  return nextCrumbs;
});

const isInCart = computed(() =>
  useCheckoutStore().isInCart(service.value?.id ?? "")
);

function toggleSelection() {
  if (!service.value) {
    throw new Error("Service is not loaded yet");
  }
  const bookmark = false;

  if (isInCart.value) {
    useCheckoutStore().removeServicesFromSelection(
      { name: serviceProvider.value.name },
      [service.value.id],
      bookmark
    );
  } else {
    useCheckoutStore().addServicesToSelection(
      {
        id: serviceProvider.value.id,
        name: serviceProvider.value.name,
      } as IBiobanks,
      [{ label: service.value.name, value: service.value.id }],
      bookmark
    );
  }
}
</script>
